import {
  BankIcon,
  CashSpecialIcon,
  Title,
  WalletIcon,
  AddCardIcon,
} from "lola-framework-ui-test";

import { useCallback, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import { getBrandInfo } from "../constants/card-brands";
import type {
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
} from "../interfaces/payment-method";
import { PayoutMethodType } from "../interfaces/payout";
import { PayoutInfo } from "./PayoutInfo";
import {
  formatBankAccountLabel,
  looksLikePayerCode,
} from "../utils/bankAccountLabel";
import { ScrollFadeIndicator } from "./ScrollFadeIndicator";
import { CloseLight } from "../assets/navIcons";

const TYPE_ROW_ORDER: PayoutMethodType[] = [
  PayoutMethodType.CASH_PICKUP,
  PayoutMethodType.MOBILE_WALLET,
];
const PICKER_SKELETON_ROWS = 3;

const PickerListSkeleton = () => (
  <>
    {Array.from({ length: PICKER_SKELETON_ROWS }, (_, i) => (
      <div key={i} className="payout-skeleton" aria-hidden="true">
        <span className="payout-skeleton-icon" />
        <span className="payout-skeleton-lines">
          <span className="payout-skeleton-line" />
          <span className="payout-skeleton-line short" />
        </span>
      </div>
    ))}
  </>
);

export type FlatPayoutPick =
  | {
      kind: "card";
      card: {
        paymentMethodId: string;
        brand: string;
        last4: string;
        cardExpiry?: string;
        cardholderName?: string;
      };
    }
  | { kind: "bank"; account: IBankAccountPaymentMethod }
  | { kind: "method"; method: PayoutMethodType }
  | { kind: "add" }
  | { kind: "addBank" };

export interface SelectPayoutMethodSlotProps {
  selectedMethod?: PayoutMethodType | null;
  selectedPaymentMethodId?: string | null;
  available: PayoutMethodType[];
  cards: ICardPaymentMethod[];
  cardsLoading?: boolean;
  accounts: IBankAccountPaymentMethod[];
  accountsLoading?: boolean;
  /** Instruments or availability still unresolved — full-list shimmer. */
  loading?: boolean;
  bankLabels?: Record<string, string>;
  onPick: (pick: FlatPayoutPick) => void;
  onClose: () => void;
}

/**
 * Flat payout picker: saved destination cards and bank accounts + type rows
 * that still need a details popup (cash / wallet) + add CTAs. No type rows
 * for PUSH_TO_CARD or BANK_ACCOUNT.
 */
export const SelectPayoutMethodSlot: React.FC<SelectPayoutMethodSlotProps> = ({
  selectedMethod = null,
  selectedPaymentMethodId = null,
  available,
  cards,
  cardsLoading = false,
  accounts,
  accountsLoading = false,
  loading = false,
  bankLabels = {},
  onPick,
  onClose,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn } = colorConfig;

  const availableSet = new Set(available);
  const cardOk = availableSet.has(PayoutMethodType.PUSH_TO_CARD);
  const bankOk = availableSet.has(PayoutMethodType.BANK_ACCOUNT);
  const typeRows = TYPE_ROW_ORDER.filter((m) => availableSet.has(m));
  const cardSubtitle = t(`payout.methods.${PayoutMethodType.PUSH_TO_CARD}`);
  const bankSubtitle = t(`payout.methods.${PayoutMethodType.BANK_ACCOUNT}`);
  const listLoading =
    loading ||
    (cardOk && cardsLoading && cards.length === 0) ||
    (bankOk && accountsLoading && accounts.length === 0);

  const iconFor = (type: PayoutMethodType): JSX.Element => {
    switch (type) {
      case PayoutMethodType.CASH_PICKUP:
        return <CashSpecialIcon size={22} colors={[bodyCopy, bodyCopy]} />;
      default:
        return <WalletIcon size={22} colors={[bodyCopy, bodyCopy]} />;
    }
  };

  const pickIcon = useCallback((brand: string) => {
    const info = getBrandInfo(brand);
    return info ? <img src={info.logo} alt={info.label} /> : <span />;
  }, []);

  const pickCard = (card: ICardPaymentMethod) => {
    const yy =
      card.expiryYear.length > 2
        ? card.expiryYear.slice(-2)
        : card.expiryYear;
    onPick({
      kind: "card",
      card: {
        paymentMethodId: card.paymentMethodId,
        brand: card.brand,
        last4: card.last4,
        cardExpiry: `${card.expiryMonth}/${yy}`,
        ...(card.cardholderName ? { cardholderName: card.cardholderName } : {}),
      },
    });
  };

  const bankTitle = (account: IBankAccountPaymentMethod): string => {
    const stored = account.bankName?.trim();
    const mapped = stored ? bankLabels[stored.toUpperCase()] : undefined;
    const name = stored && !looksLikePayerCode(stored) ? stored : mapped;
    return (
      formatBankAccountLabel(name, account.maskedAccountNumber) ??
      name ??
      bankSubtitle
    );
  };

  return (
    <section className="select-payer-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title title={t("payout.title")} align="center" color={bodyCopy} />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <section
        className="select-payer-list select-funding-list"
        aria-busy={listLoading}
        style={
          {
            "--bgIcon": backgroundBtn,
            "--iconOnBrand": textColorBtn,
          } as React.CSSProperties
        }
      >
        <ScrollFadeIndicator color={bodyCopy} />
        {listLoading ? <PickerListSkeleton /> : null}
        {!listLoading && cardOk &&
          cards.map((card) => {
            const displayName = `${(card.brand || "").toUpperCase()}-${card.last4}`;
            const isSelected =
              selectedPaymentMethodId === card.paymentMethodId;
            return (
              <PayoutInfo
                key={card.paymentMethodId}
                title={displayName}
                subtitle={cardSubtitle}
                icon={pickIcon(card.brand)}
                className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
                onChange={() => pickCard(card)}
              />
            );
          })}
        {!listLoading && bankOk &&
          accounts.map((account) => {
            const isSelected =
              selectedPaymentMethodId === account.paymentMethodId;
            return (
              <PayoutInfo
                key={account.paymentMethodId}
                title={bankTitle(account)}
                subtitle={bankSubtitle}
                icon={<BankIcon size={22} colors={[bodyCopy, bodyCopy]} />}
                className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
                onChange={() => onPick({ kind: "bank", account })}
              />
            );
          })}
        {!listLoading && typeRows.map((type) => {
          const isSelected = selectedMethod === type;
          return (
            <PayoutInfo
              key={type}
              title={t(`payout.methods.${type}`)}
              subtitle=""
              icon={iconFor(type)}
              className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
              onChange={() => onPick({ kind: "method", method: type })}
            />
          );
        })}
        {!listLoading && cardOk && (
          <PayoutInfo
            title={t("payment.addDebitCard")}
            subtitle={cardSubtitle}
            className="funding-brand-icon"
            icon={
              <AddCardIcon size={22} colors={[textColorBtn, textColorBtn]} />
            }
            onChange={() => onPick({ kind: "add" })}
          />
        )}
        {!listLoading && bankOk && (
          <PayoutInfo
            title={t("payment.addBankAccount")}
            subtitle={bankSubtitle}
            className="funding-brand-icon"
            icon={<BankIcon size={22} colors={[textColorBtn, textColorBtn]} />}
            onChange={() => onPick({ kind: "addBank" })}
          />
        )}
      </section>
    </section>
  );
};
